import axios from 'axios'
import React, { useState } from 'react'
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap'
import { renameSegmentKeys } from '@thairath/dmp-segment'

const ERROR_MESSAGES = {
    MISSING_QUERY_PARAMETERS: 'กรุณาเลือกข้อมูลที่ต้องการ Export',
    NO_DATA_FOUND: 'ไม่พบข้อมูลที่ต้องการ Export',
    BAD_REQUEST: 'ข้อมูลที่ส่งไปไม่ถูกต้อง กรุณาตรวจสอบและลองใหม่อีกครั้ง',
    NOT_FOUND: 'ไม่พบข้อมูลที่ต้องการ Export',
    SERVER_ERROR: 'เกิดข้อผิดพลาดของเซิร์ฟเวอร์ กรุณาลองใหม่อีกครั้ง',
    NETWORK_ERROR: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต',
    DEFAULT: 'เกิดข้อผิดพลาดในการ Export กรุณาลองใหม่อีกครั้ง'
}

const ExportSegmentModal = ({ open, close, segmentData }) => {
    const [selectedFields, setSelectedFields] = useState({
        'line-user-id': false,
        'email': false,
        'mobile-number': false
    })
    const [isExporting, setIsExporting] = useState(false)
    const [error, setError] = useState(null)

    const handleCheckboxChange = (field) => {
        setSelectedFields(prev => ({
            ...prev,
            [field]: !prev[field]
        }))
    }

    const getErrorMessageFromCode = (code) => {
        const errorMap = {
            'MISSING_QUERY_PARAMETERS': ERROR_MESSAGES.MISSING_QUERY_PARAMETERS,
            'NO_DATA_FOUND': ERROR_MESSAGES.NO_DATA_FOUND
        }
        return errorMap[code] || (code ? `เกิดข้อผิดพลาด: ${code}` : null)
    }

    const parseBlobError = async (blob) => {
        try {
            const text = await blob.text()
            const jsonError = JSON.parse(text)
            return getErrorMessageFromCode(jsonError.message)
        } catch (parseError) {
            console.error('Error parsing blob:', parseError)
            return null
        }
    }

    const getHttpStatusError = (status) => {
        const statusErrors = {
            400: ERROR_MESSAGES.BAD_REQUEST,
            404: ERROR_MESSAGES.NOT_FOUND,
            500: ERROR_MESSAGES.SERVER_ERROR
        }
        return statusErrors[status]
    }

    const parseErrorMessage = async (error) => {
        if (!error.response) {
            return error.request ? ERROR_MESSAGES.NETWORK_ERROR : ERROR_MESSAGES.DEFAULT
        }

        const { status, data } = error.response

        if (data instanceof Blob) {
            const blobError = await parseBlobError(data)
            if (blobError) return blobError
        }

        const statusError = getHttpStatusError(status)
        if (statusError && status !== 404) return statusError

        if (data?.message) {
            const messageError = getErrorMessageFromCode(data.message)
            if (messageError) return messageError
        }

        if (status === 404) return ERROR_MESSAGES.NOT_FOUND

        return ERROR_MESSAGES.DEFAULT
    }

    const handleExport = async () => {
        const selected = Object.keys(selectedFields).filter(key => selectedFields[key])

        if (selected.length === 0) {
            setError('กรุณาเลือกข้อมูลที่ต้องการ Export อย่างน้อย 1 รายการ')
            return
        }

        const renameSegment = renameSegmentKeys(segmentData.segmentsCondition)
        setIsExporting(true)
        setError(null)

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_ROOT}/segment/export`,
                {
                    segmentId: segmentData._id,
                    segments: segmentData.segments,
                    segmentsCondition: renameSegment,
                    lineId: selectedFields['line-user-id'],
                    email: selectedFields['email'],
                    number: selectedFields['mobile-number'],
                },
                {
                    responseType: 'blob',
                    withCredentials: true
                }
            )

            const blob = new Blob([res.data], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `segment-export-${Date.now()}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
            setIsExporting(false)
            handleClose()
        } catch (error) {
            console.error('Export error:', error)
            const errorMessage = await parseErrorMessage(error)
            setError(errorMessage)
            setIsExporting(false)
        }
    }

    const handleClose = () => {
        if (isExporting) return

        setSelectedFields({
            'line-user-id': false,
            'email': false,
            'mobile-number': false
        })
        setError(null)
        close()
    }

    return (
        <Modal show={open} onHide={handleClose} centered backdrop={isExporting ? 'static' : true}>
            <Modal.Header closeButton={!isExporting}>
                <Modal.Title>Export</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
                        <p className="mb-0">{error}</p>
                    </Alert>
                )}

                {isExporting ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status" variant="success">
                            <span className="sr-only">Loading...</span>
                        </Spinner>
                        <div className="mt-3">กำลัง Export ข้อมูล...</div>
                        <div className="text-muted mt-2">กรุณารอสักครู่</div>
                    </div>
                ) : (
                    <Form>
                        <Form.Group>
                            <Form.Check
                                type="checkbox"
                                id="line-user-id"
                                label="Line user ID"
                                checked={selectedFields['line-user-id']}
                                onChange={() => handleCheckboxChange('line-user-id')}
                                className="mb-2"
                            />
                            <Form.Check
                                type="checkbox"
                                id="email"
                                label="Email"
                                checked={selectedFields['email']}
                                onChange={() => handleCheckboxChange('email')}
                                className="mb-2"
                            />
                            <Form.Check
                                type="checkbox"
                                id="mobile-number"
                                label="Mobile number"
                                checked={selectedFields['mobile-number']}
                                onChange={() => handleCheckboxChange('mobile-number')}
                                className="mb-2"
                            />
                        </Form.Group>
                    </Form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isExporting}>
                    ยกเลิก
                </Button>
                <Button variant="success" onClick={handleExport} disabled={isExporting}>
                    {isExporting ? (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                            />
                            Exporting...
                        </div>
                    ) : (
                        <>
                            <i className="fas fa-download"></i> Export
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default ExportSegmentModal